import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSuggestionCount,
  getSuggestions,
  executeSuggestion,
  updateSuggestion,
  patchSuggestion,
  convertToTask,
  batchUpdateSuggestions,
  batchExecuteSuggestions,
  sendVoiceCommand,
} from '../../api/meetingInsights';
import { safeUrl } from '../../utils/safeUrl';
import './InsightsBadge.css';

const POLL_INTERVAL = 5 * 60_000;
// Meeting insights badge visible to all authenticated users
const JIRA_BASE_URL = process.env.REACT_APP_JIRA_BASE_URL || '';

/** Parse markdown links [text](url) and plain URLs into clickable elements. */
function linkify(text) {
  if (!text) return null;
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    const mdMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (mdMatch) return <a key={i} href={safeUrl(mdMatch[2])} target="_blank" rel="noopener noreferrer">{mdMatch[1]}</a>;
    if (/^https?:\/\//.test(part)) {
      let label = 'link';
      if (part.includes('docs.google.com')) label = 'Transcript';
      else if (part.includes('meet.google.com') || part.includes('drive.google.com')) label = 'Recording';
      else if (part.includes('atlassian.net')) label = part.match(/PROJ-\d+/)?.[0] || 'Jira';
      return <a key={i} href={safeUrl(part)} target="_blank" rel="noopener noreferrer">{label}</a>;
    }
    return part;
  });
}

/** Strip the comment header (Meeting update line + links) from body text */
function stripHeader(text) {
  if (!text) return text;
  // Remove everything up to and including the blank line after the header
  // Handles both markdown link format and plain text format
  let stripped = text;
  // Remove lines starting with "Meeting update" or "[Meeting update" (markdown link)
  stripped = stripped.replace(/^(?:\[Meeting update[^\]]*\]\([^)]*\)|Meeting update[^\n]*)\n?/m, '');
  // Remove Transcript/Recording links line
  stripped = stripped.replace(/^(?:\[Transcript\][^\n]*|\s*Transcript[^\n]*)\n?/m, '');
  // Remove leftover blank lines at start
  stripped = stripped.replace(/^\n+/, '');
  return stripped.trim();
}

export default function InsightsBadge() {
  const { getAuthHeader, user } = useAuth();
  const auth = getAuthHeader();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({});
  const [accepting, setAccepting] = useState(new Set());
  // Issue selector state
  const [issueSelector, setIssueSelector] = useState(null); // { suggestionId }
  const [issueSearch, setIssueSearch] = useState('');
  const [issueResults, setIssueResults] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  // User selector state
  const [userSelector, setUserSelector] = useState(null); // { suggestionId }
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  // Due dates per suggestion
  const [dueDates, setDueDates] = useState({});
  // Result messages per suggestion
  const [results, setResults] = useState({});
  // Converting comment to task
  const [converting, setConverting] = useState(new Set());
  // Voice recording state per suggestion
  const [voiceRecording, setVoiceRecording] = useState(null); // suggestion id being recorded
  const [voiceProcessing, setVoiceProcessing] = useState(null); // suggestion id being processed
  const [voiceTranscript, setVoiceTranscript] = useState(null); // { id, text }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Meeting filter (free text + eventId pinned from ?meeting=...)
  const [filterText, setFilterText] = useState('');
  const [pinnedEventId, setPinnedEventId] = useState(null);

  const intervalRef = useRef(null);
  const popupRef = useRef(null);
  const allowed = !!user?.email;

  const fetchCount = useCallback(async () => {
    try {
      const data = await getSuggestionCount(auth, true);
      if (data.ok) setCount(data.pending || 0);
    } catch { /* silent */ }
  }, [auth]);

  useEffect(() => {
    if (!allowed) return;
    fetchCount();
    intervalRef.current = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [allowed, fetchCount]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        if (!issueSelector && !userSelector) setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, issueSelector, userSelector]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await getSuggestions('pending', auth, { forMe: true });
      if (data.ok) {
        const items = data.suggestions || [];
        setSuggestions(items);
        // Set default due dates for tasks (7 days from now)
        const defaultDue = new Date(Date.now() + 7 * 86400_000).toISOString().split('T')[0];
        const dates = {};
        items.forEach(s => { if (s.type === 'jira_task') dates[s.id] = defaultDue; });
        setDueDates(prev => ({ ...prev, ...dates }));
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleOpen = () => { setOpen(true); loadSuggestions(); };

  // Auto-open popup if ?meeting=<eventId> is present in URL
  useEffect(() => {
    if (!allowed) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const meetingId = params.get('meeting');
      if (meetingId) {
        setPinnedEventId(meetingId);
        setOpen(true);
        loadSuggestions();
      }
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const clearFilter = () => {
    setFilterText('');
    setPinnedEventId(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('meeting');
      window.history.replaceState({}, '', url.toString());
    } catch { /* silent */ }
  };

  // Resolve the label for the pinned meeting (from suggestions already loaded).
  // Falls back to the raw eventId if no suggestion for it is in scope.
  const pinnedMeetingLabel = (() => {
    if (!pinnedEventId) return null;
    const match = suggestions.find(s => s.event_id === pinnedEventId);
    if (match) {
      return {
        title: match.event_summary || 'Meeting',
        date: match.event_date_utc || '',
      };
    }
    return { title: pinnedEventId, date: '' };
  })();

  // Filter suggestions: meeting pin and search text are ANDed together so a
  // typed query always narrows the result set, never widens it. The hay
  // string is the JSON of every primitive field on the suggestion so the
  // search really does cover "all fields" (was previously a hand-picked
  // subset that missed e.g. confidence, project key, jiraId).
  const buildHay = (s) => {
    const parts = [];
    const walk = (val) => {
      if (val == null) return;
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        parts.push(String(val));
      } else if (Array.isArray(val)) {
        val.forEach(walk);
      } else if (typeof val === 'object') {
        Object.values(val).forEach(walk);
      }
    };
    walk(s);
    parts.push(s.type === 'jira_comment' ? 'comment' : (s.type === 'jira_task' ? 'task new task' : ''));
    return parts.join(' \n ').toLowerCase();
  };

  const filterSuggestions = () => {
    const needle = filterText.trim().toLowerCase();
    const hasFilter = pinnedEventId || needle;
    if (!hasFilter) return suggestions;
    return suggestions.filter(s => {
      if (pinnedEventId && s.event_id !== pinnedEventId) return false;
      if (needle && !buildHay(s).includes(needle)) return false;
      return true;
    });
  };

  // Wrap occurrences of `needle` in <mark> so the user can see WHY a card
  // matched. Case-insensitive, all-occurrences. Pure passthrough when no
  // search is active (returns the original string).
  const highlight = (text, needle) => {
    if (!text || !needle) return text;
    const txt = String(text);
    const lc = txt.toLowerCase();
    const ndl = needle.toLowerCase();
    if (!lc.includes(ndl)) return txt;
    const out = [];
    let pos = 0;
    while (pos < txt.length) {
      const idx = lc.indexOf(ndl, pos);
      if (idx < 0) { out.push(txt.slice(pos)); break; }
      if (idx > pos) out.push(txt.slice(pos, idx));
      out.push(<mark key={idx} className="insights-search-hit">{txt.slice(idx, idx + ndl.length)}</mark>);
      pos = idx + ndl.length;
    }
    return <>{out}</>;
  };

  const handleAccept = async (id) => {
    setAccepting(prev => new Set([...prev, id]));
    const s = suggestions.find(x => x.id === id);
    const overrides = {};
    if (s?.type === 'jira_task' && dueDates[id]) overrides.dueDate = dueDates[id];
    if (s?.suggested_assignee_jira_id) overrides.assigneeId = s.suggested_assignee_jira_id;
    if (s?.target_issue_key) overrides.target_issue_key = s.target_issue_key;
    if (s?._originalAssigneeId) overrides.addToNeedToKnow = s._originalAssigneeId;
    const result = await executeSuggestion(id, auth, overrides);
    setAccepting(prev => { const c = new Set(prev); c.delete(id); return c; });
    if (result.ok) {
      const issueKey = result.result?.issueKey;
      setResults(prev => ({ ...prev, [id]: { type: 'accepted', issueKey } }));
      setCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleReject = async (id) => {
    await updateSuggestion(id, 'reject', null, auth);
    setResults(prev => ({ ...prev, [id]: { type: 'rejected' } }));
    setCount(prev => Math.max(0, prev - 1));
  };

  const handleConvertToTask = async (id) => {
    setConverting(prev => new Set([...prev, id]));
    const result = await convertToTask(id, auth);
    setConverting(prev => { const c = new Set(prev); c.delete(id); return c; });
    if (result.ok) {
      const defaultDue = new Date(Date.now() + 7 * 86400_000).toISOString().split('T')[0];
      setSuggestions(prev => prev.map(s => s.id === id ? {
        ...s,
        type: 'jira_task',
        suggested_summary: result.title,
        suggested_description: result.description,
        comment_text: null,
        modified_text: null,
        target_issue_key: null,
        target_issue_summary: null,
      } : s));
      setDueDates(prev => ({ ...prev, [id]: defaultDue }));
    }
  };

  // ── Voice recording ──────────────────────────────────────────────

  const startVoice = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 }
      });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) { setVoiceRecording(null); return; }
        await processVoice(id, blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setVoiceRecording(id);
    } catch (err) {
      console.error('Mic access failed:', err);
    }
  };

  const stopVoice = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setVoiceRecording(null);
  };

  const processVoice = async (id, blob) => {
    setVoiceProcessing(id);
    setVoiceTranscript(null);
    try {
      const result = await sendVoiceCommand(id, blob, auth);
      if (result.ok) {
        setVoiceTranscript({ id, text: result.transcript });

        // Apply updates to local suggestion
        if (result.updates) {
          setSuggestions(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s };
            if (result.updates.type) updated.type = result.updates.type;
            if (result.updates.target_issue_key !== undefined) updated.target_issue_key = result.updates.target_issue_key;
            if (result.updates.person_name) updated.person_name = result.updates.person_name;
            if (result.updates.person_jira_id) updated.person_jira_id = result.updates.person_jira_id;
            if (result.updates.suggested_assignee_jira_id) updated.suggested_assignee_jira_id = result.updates.suggested_assignee_jira_id;
            if (result.updates.modified_text !== undefined) updated.modified_text = result.updates.modified_text;
            if (result.updates.suggested_summary) updated.suggested_summary = result.updates.suggested_summary;
            if (result.updates.suggested_description) updated.suggested_description = result.updates.suggested_description;
            if (result.updates.suggested_duedate) updated.suggested_duedate = result.updates.suggested_duedate;
            return updated;
          }));

          // Update due date if set
          if (result.updates.suggested_duedate) {
            setDueDates(prev => ({ ...prev, [id]: result.updates.suggested_duedate }));
          }
        }

        // Handle termination
        if (result.termination === 'accept') {
          const issueKey = result.executeResult?.issueKey;
          setResults(prev => ({ ...prev, [id]: { type: 'accepted', issueKey } }));
          setCount(prev => Math.max(0, prev - 1));
        } else if (result.termination === 'reject') {
          setResults(prev => ({ ...prev, [id]: { type: 'rejected' } }));
          setCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      console.error('Voice command failed:', err);
    }
    setVoiceProcessing(null);
  };

  // Bulk handlers operate only on the cards the user can actually SEE right now:
  // the active meeting/text filter (filterSuggestions) AND not already resolved
  // (`results`). This is critical — without the filter, "Accept all" on a page
  // showing an empty filtered list would still action every hidden pending item
  // from OTHER meetings. "Accept all N" must only ever mean the N on screen.
  const liveIds = () => filterSuggestions().filter(s => !results[s.id]).map(s => s.id);

  const handleAcceptAll = async () => {
    const ids = liveIds();
    if (ids.length === 0) return;
    setAccepting(new Set(ids));
    const r = await batchExecuteSuggestions(ids, auth);
    // Mark each as accepted in the local state so counters and per-card
    // result chips update; preserve any already-resolved ones.
    setResults(prev => {
      const next = { ...prev };
      for (const id of ids) {
        if (next[id]) continue;
        const issueKey = r?.results?.find(x => x.id === id)?.issueKey;
        next[id] = { type: 'accepted', issueKey };
      }
      return next;
    });
    setCount(prev => Math.max(0, prev - ids.length));
    setAccepting(new Set());
  };

  const handleRejectAll = async () => {
    const ids = liveIds();
    if (ids.length === 0) return;
    await batchUpdateSuggestions(ids, 'reject', auth);
    setResults(prev => {
      const next = { ...prev };
      for (const id of ids) if (!next[id]) next[id] = { type: 'rejected' };
      return next;
    });
    setCount(prev => Math.max(0, prev - ids.length));
  };

  // ── Issue selector ────────────────────────────────────────────────────

  const openIssueSelector = async (suggestionId) => {
    setIssueSelector({ suggestionId });
    setIssueSearch('');
    setLoadingIssues(true);
    try {
      const res = await fetch('/api/jira/issues', { headers: { Authorization: auth } });
      const data = await res.json();
      setIssueResults(data.issues || []);
    } catch { /* silent */ }
    setLoadingIssues(false);
  };

  const selectIssue = (issue) => {
    const sid = issueSelector.suggestionId;
    setSuggestions(prev => prev.map(s => s.id === sid ? {
      ...s,
      target_issue_key: issue.key,
      target_issue_summary: issue.fields?.summary || issue.summary,
    } : s));
    setIssueSelector(null);
  };

  const filteredIssues = issueSearch.trim()
    ? issueResults.filter(i =>
        i.key.toLowerCase().includes(issueSearch.toLowerCase()) ||
        (i.fields?.summary || '').toLowerCase().includes(issueSearch.toLowerCase())
      ).slice(0, 50)
    : issueResults.slice(0, 50);

  // ── User selector ─────────────────────────────────────────────────────

  const openUserSelector = async (suggestionId) => {
    setUserSelector({ suggestionId });
    setUserSearch('');
    if (allUsers.length === 0) {
      setLoadingUsers(true);
      try {
        const res = await fetch('/api/jira/users/lookup', { headers: { Authorization: auth } });
        const data = await res.json();
        // API returns { lookup: { jiraId: displayName } }
        const users = Object.entries(data.lookup || {}).map(([accountId, displayName]) => ({
          accountId,
          displayName,
        }));
        users.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setAllUsers(users);
      } catch { /* silent */ }
      setLoadingUsers(false);
    }
  };

  const selectUser = (u) => {
    const sid = userSelector.suggestionId;
    let originalAssignee = null;
    setSuggestions(prev => prev.map(s => {
      if (s.id !== sid) return s;
      // Save original assignee before reassigning (for need-to-know)
      originalAssignee = s._originalAssigneeId || s.original_assignee_jira_id || s.suggested_assignee_jira_id || s.person_jira_id;
      return {
        ...s,
        person_name: u.displayName,
        person_jira_id: u.accountId,
        suggested_assignee_jira_id: u.accountId,
        _originalAssigneeId: originalAssignee,
      };
    }));
    setUserSelector(null);
    // M-8: persist the reassignment server-side so batch-accept honors
    // the original assignee for need-to-know. Old code kept this in local
    // React state only; bulk "Accept all" went straight to executeSuggestion
    // without overrides and the original assignee was lost.
    if (originalAssignee) {
      patchSuggestion(sid, {
        suggested_assignee_jira_id: u.accountId,
        original_assignee_jira_id: originalAssignee,
      }, auth).catch(err => console.warn('[MI] persist reassign failed:', err?.message));
    }
  };

  const filteredUsers = userSearch.trim()
    ? allUsers.filter(u =>
        u.displayName?.toLowerCase().includes(userSearch.toLowerCase())
      )
    : allUsers;

  // Top-of-popup counts. Used in the summary subtitle and the bulk action
  // buttons so the user can see *what* "Accept all" actually accepts before
  // they click — the "context before affordances" rule.
  //
  // We exclude already-resolved cards (`results[id]` is set the moment a
  // user accepts/rejects/converts) so the counters tick down as the user
  // works through the list. Without this filter, the header would still
  // say "5 possible comments" after the user had already accepted four,
  // and "Accept all 5" would re-trigger work on the resolved cards.
  //
  // It ALSO respects the active meeting/text filter (filterSuggestions), so the
  // summary counts and the bulk buttons always match the visible list. A stale
  // ?meeting= deep-link to an already-handled meeting now shows 0 here (empty,
  // no bulk buttons) instead of the old counter-says-7 / list-empty mismatch.
  const liveSuggestions = filterSuggestions().filter(s => !results[s.id]);
  const commentCount = liveSuggestions.filter(s => s.type === 'jira_comment').length;
  const taskCount = liveSuggestions.filter(s => s.type === 'jira_task').length;
  const liveCount = liveSuggestions.length;
  const summaryParts = [];
  if (commentCount > 0) summaryParts.push(`${commentCount} possible comment${commentCount !== 1 ? 's' : ''}`);
  if (taskCount > 0) summaryParts.push(`${taskCount} possible new task${taskCount !== 1 ? 's' : ''}`);
  const summarySentence = summaryParts.length
    ? `From your recent meetings we found ${summaryParts.join(' and ')}. Review below.`
    : null;

  if (!allowed) return null;
  if (count === 0 && !open) return null;


  return (
    <div className="insights-badge-wrapper">
      {count > 0 && (
        <button
          className="insights-badge-btn"
          title={`${count} item${count !== 1 ? 's' : ''} in your PM Inbox`}
          onClick={handleOpen}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
          </svg>
          <span className="insights-badge-dot">{count > 99 ? '99+' : count}</span>
        </button>
      )}

      {open && (
        <div className="insights-popup-overlay">
          <div className="insights-popup" ref={popupRef}>
            <div className="insights-popup-header">
              <div className="insights-popup-title-row">
                <h3>PM Inbox</h3>
                <button className="insights-popup-close" onClick={() => setOpen(false)} aria-label="Close">&#10005;</button>
              </div>
              {summarySentence && (
                <p className="insights-popup-summary">{summarySentence}</p>
              )}
              {liveCount > 1 && (
                <div className="insights-popup-bulk">
                  <button className="ip-btn ip-btn-accept ip-btn-bulk" onClick={handleAcceptAll}>
                    Accept all {liveCount}
                  </button>
                  <button className="ip-btn ip-btn-reject ip-btn-bulk" onClick={handleRejectAll}>
                    Reject all {liveCount}
                  </button>
                </div>
              )}
            </div>

            {pinnedEventId && (
              <div className="insights-popup-pin">
                <span className="insights-popup-pin-icon" aria-hidden="true">📅</span>
                <div className="insights-popup-pin-body">
                  <div className="insights-popup-pin-label">Filtered by meeting</div>
                  <div className="insights-popup-pin-meeting">
                    {pinnedMeetingLabel?.title}
                    {pinnedMeetingLabel?.date && (
                      <span className="insights-popup-pin-date"> · {pinnedMeetingLabel.date}</span>
                    )}
                  </div>
                </div>
                <button
                  className="insights-popup-pin-clear"
                  onClick={clearFilter}
                  title="Clear meeting filter"
                  aria-label="Clear meeting filter"
                >✕</button>
              </div>
            )}

            <div className="insights-popup-filter">
              <div className="insights-popup-filter-label">
                Narrow the focus
                <span className="insights-popup-filter-hint">— filter to a specific meeting, person, or issue</span>
              </div>
              <div className="insights-popup-filter-row">
                <input
                  type="text"
                  className="insights-popup-filter-input"
                  placeholder="Search…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                />
                {(filterText || pinnedEventId) && (
                  <button className="ip-btn ip-btn-secondary ip-btn-mini" onClick={clearFilter}>Clear</button>
                )}
              </div>
            </div>

            <div className="insights-popup-body">
              {loading ? (
                <div className="insights-popup-empty">Loading...</div>
              ) : suggestions.length === 0 ? (
                <div className="insights-popup-empty">No pending suggestions for you.</div>
              ) : (() => {
                const needle = filterText.trim();
                const hl = (t) => highlight(t, needle);
                const renderCard = (s) => {
                  const isEditing = editing[s.id] != null;
                  const isAccepting = accepting.has(s.id);
                  const rawBody = s.type === 'jira_comment'
                    ? (s.modified_text || s.comment_text)
                    : (s.modified_text || s.suggested_summary || '');
                  const bodyText = stripHeader(rawBody) || '';
                  const startEditing = () => {
                    if (s.type === 'jira_task') {
                      setEditing(prev => ({
                        ...prev,
                        [s.id]: {
                          summary: s.suggested_summary || '',
                          description: stripHeader(s.suggested_description || '') || '',
                        },
                      }));
                    } else {
                      setEditing(prev => ({ ...prev, [s.id]: { body: bodyText } }));
                    }
                  };
                  const issueKey = s.target_issue_key;
                  const result = results[s.id];

                  if (result) {
                    return (
                      <div key={s.id} className={`insights-card insights-card-result ${result.type}`}>
                        {result.type === 'accepted' ? (
                          <span>Created{result.issueKey ? <>{': '}<a href={safeUrl(`${JIRA_BASE_URL}/${result.issueKey}`)} target="_blank" rel="noopener noreferrer">{result.issueKey}</a></> : ''}</span>
                        ) : (
                          <span>Rejected</span>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={s.id} className={`insights-card ${s.type === 'jira_comment' ? 'insights-card-comment' : 'insights-card-task'}`}>
                      {/* Line 1: Meeting context with the literal "Meeting:" prefix
                          so the user instantly knows what they're looking at. */}
                      <div className="insights-card-row insights-card-meeting">
                        <span className="insights-card-key">Meeting:</span>{' '}
                        {s.event_link
                          ? <a href={safeUrl(s.event_link)} target="_blank" rel="noopener noreferrer">{hl(s.event_summary)}</a>
                          : <span>{hl(s.event_summary)}</span>
                        }
                        {' · '}{hl(s.event_date_utc)}
                        {s.transcript_link && <>{' · '}<a href={safeUrl(s.transcript_link)} target="_blank" rel="noopener noreferrer">Transcript</a></>}
                        {s.recording_link && <>{' · '}<a href={safeUrl(s.recording_link)} target="_blank" rel="noopener noreferrer">Recording</a></>}
                      </div>

                      {/* Target row: tells the user what the suggestion is
                          attached to (an existing Jira issue for comments, a
                          person for tasks) and lets them swap that target.
                          The "Suggested comment / Suggested new task" type
                          label is gone — the card tint + the bold body
                          header below ("Add new comment:" / "Add new task:")
                          already convey the type. */}
                      {/* Target row uses the same Label: value pattern as
                          Meeting / Add new comment so every row in the card
                          reads the same way. The pick-another / create-new
                          buttons sit on the next row so the key:value line
                          isn't crowded. */}
                      {s.type === 'jira_comment' ? (
                        <>
                          <div className="insights-card-row insights-card-target">
                            <span className="insights-card-key">Update task:</span>{' '}
                            {issueKey ? (
                              <span className="insights-card-value">
                                <a href={safeUrl(`${JIRA_BASE_URL}/${issueKey}`)} target="_blank" rel="noopener noreferrer">
                                  {hl(`${issueKey}: ${s.target_issue_summary}`)}
                                </a>
                              </span>
                            ) : (
                              <span className="insights-card-value insights-card-warn">no target issue selected</span>
                            )}
                          </div>
                          <div className="insights-card-row">
                            <button className="ip-btn ip-btn-secondary ip-btn-mini" onClick={() => openIssueSelector(s.id)}>
                              {issueKey ? 'Select another task' : 'Select Jira task'}
                            </button>{' '}
                            <button
                              className="ip-btn ip-btn-secondary ip-btn-mini"
                              disabled={converting.has(s.id)}
                              onClick={() => handleConvertToTask(s.id)}
                            >{converting.has(s.id) ? 'Creating…' : 'Create new task'}</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="insights-card-row insights-card-target">
                            <span className="insights-card-key">Assigned to:</span>{' '}
                            <span className="insights-card-value"><strong>{hl(s.person_name)}</strong></span>
                          </div>
                          <div className="insights-card-row">
                            <button className="ip-btn ip-btn-secondary ip-btn-mini" onClick={() => openUserSelector(s.id)}>Reassign</button>
                          </div>
                        </>
                      )}

                      {isEditing ? (
                        <>
                          {s.type === 'jira_task' ? (
                            <>
                              <div className="insights-edit-field">
                                <label className="insights-field-label">Summary</label>
                                <textarea
                                  className="insights-edit insights-edit-summary"
                                  value={editing[s.id]?.summary ?? ''}
                                  onChange={e => setEditing(prev => ({ ...prev, [s.id]: { ...prev[s.id], summary: e.target.value } }))}
                                />
                              </div>
                              <div className="insights-edit-field">
                                <label className="insights-field-label">Description</label>
                                <textarea
                                  className="insights-edit insights-edit-description"
                                  rows={6}
                                  value={editing[s.id]?.description ?? ''}
                                  onChange={e => setEditing(prev => ({ ...prev, [s.id]: { ...prev[s.id], description: e.target.value } }))}
                                />
                              </div>
                            </>
                          ) : (
                            <textarea
                              className="insights-edit"
                              value={editing[s.id]?.body ?? ''}
                              onChange={e => setEditing(prev => ({ ...prev, [s.id]: { ...prev[s.id], body: e.target.value } }))}
                            />
                          )}
                          <div className="insights-card-btns">
                            <button className="ip-btn ip-btn-accept" onClick={async () => {
                              const edit = editing[s.id] || {};
                              if (s.type === 'jira_task') {
                                const updates = {
                                  suggested_summary: edit.summary ?? s.suggested_summary,
                                  suggested_description: edit.description ?? s.suggested_description,
                                };
                                await patchSuggestion(s.id, updates, auth);
                                setSuggestions(prev => prev.map(x => x.id === s.id ? { ...x, ...updates } : x));
                              } else {
                                await updateSuggestion(s.id, 'modify', edit.body ?? '', auth);
                                setSuggestions(prev => prev.map(x => x.id === s.id ? { ...x, modified_text: edit.body ?? '' } : x));
                              }
                              setEditing(prev => { const c = {...prev}; delete c[s.id]; return c; });
                            }}>Save</button>
                            <button className="ip-btn ip-btn-secondary" onClick={() =>
                              setEditing(prev => { const c = {...prev}; delete c[s.id]; return c; })
                            }>Cancel</button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Body content. Edit sits on its own row right
                              underneath, so long body text doesn't push the
                              button off the side. Same mini-button styling
                              as Select another task. */}
                          {s.type === 'jira_task' ? (
                            <>
                              <div className="insights-card-row">
                                <span className="insights-card-key">Add new task:</span>{' '}
                                <span className="insights-card-value">{hl(s.modified_text || s.suggested_summary)}</span>
                              </div>
                              {stripHeader(s.suggested_description || '') && (
                                <div className="insights-card-row">
                                  <span className="insights-card-key">Description:</span>{' '}
                                  <span className="insights-card-value">{hl(stripHeader(s.suggested_description || ''))}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="insights-card-row">
                              <span className="insights-card-key">Add new comment:</span>{' '}
                              <span className="insights-card-value">{hl(bodyText)}</span>
                            </div>
                          )}
                          <div className="insights-card-row">
                            <button className="ip-btn ip-btn-secondary ip-btn-mini" onClick={startEditing}>Edit</button>
                          </div>

                          {s.transcript_quote && (
                            <div className="insights-card-quote">{hl(s.transcript_quote)}</div>
                          )}

                          {s.type === 'jira_task' && (
                            <div className="insights-card-row insights-card-due">
                              <span className="insights-card-key">Due:</span>{' '}
                              <input
                                type="date"
                                value={dueDates[s.id] || ''}
                                onChange={e => setDueDates(prev => ({ ...prev, [s.id]: e.target.value }))}
                              />
                            </div>
                          )}

                          {voiceProcessing === s.id && (
                            <div className="insights-voice-processing">
                              <div className="insights-voice-spinner" />
                              <span>Processing voice command...</span>
                            </div>
                          )}

                          {voiceTranscript?.id === s.id && (
                            <div className="insights-voice-transcript">
                              You said: "{voiceTranscript.text}"
                            </div>
                          )}

                          <div className="insights-card-btns">
                            {(() => {
                              const needsTarget = s.type === 'jira_comment' && !issueKey;
                              return (
                                <button
                                  className="ip-btn ip-btn-accept"
                                  disabled={isAccepting || needsTarget}
                                  title={needsTarget ? 'Pick a target issue or convert to a new task before accepting' : undefined}
                                  onClick={() => handleAccept(s.id)}
                                >
                                  {isAccepting ? 'Creating…' : 'Accept'}
                                </button>
                              );
                            })()}
                            <button className="ip-btn ip-btn-reject" onClick={() => handleReject(s.id)}>Reject</button>
                            {voiceRecording === s.id ? (
                              <button className="ip-btn ip-btn-voice recording" onClick={stopVoice} title="Stop recording">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                              </button>
                            ) : (
                              <button
                                className="ip-btn ip-btn-voice"
                                disabled={voiceProcessing === s.id}
                                onClick={() => startVoice(s.id)}
                                title="Voice command"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                  <line x1="12" y1="19" x2="12" y2="23"/>
                                  <line x1="8" y1="23" x2="16" y2="23"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                };
                const matched = filterSuggestions();
                const hasFilter = !!(pinnedEventId || filterText.trim());

                // Group cards by suggestion type so the layout reads
                // top-to-bottom with clear sections.
                const renderTypeSection = (label, items) => items.length > 0 && (
                  <div className="insights-type-section">
                    <h4 className="insights-section-header">{label} <span className="insights-section-count">({items.length})</span></h4>
                    {items.map(renderCard)}
                  </div>
                );
                const comments = matched.filter(s => s.type === 'jira_comment');
                const tasks = matched.filter(s => s.type === 'jira_task');

                if (matched.length === 0) {
                  return <div className="insights-popup-empty">
                    {pinnedEventId
                      ? (count > 0
                          ? `No pending items for this meeting — they may already be handled. Clear the filter above to see your ${count} other item${count !== 1 ? 's' : ''}.`
                          : 'No pending items for this meeting — they may already be handled.')
                      : (hasFilter ? 'No suggestions match your search criteria.' : 'No pending suggestions for you.')}
                  </div>;
                }
                return (
                  <>
                    {renderTypeSection('Comments', comments)}
                    {renderTypeSection('New tasks', tasks)}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Issue Selector Panel */}
          {issueSelector && (
            <div className="insights-selector-overlay" onClick={() => setIssueSelector(null)}>
              <div className="insights-selector" onClick={e => e.stopPropagation()}>
                <div className="insights-selector-header">
                  <h3>Select Jira Task</h3>
                  <button className="insights-popup-close" onClick={() => setIssueSelector(null)}>&#10005;</button>
                </div>
                <div className="insights-selector-search">
                  <input
                    type="text"
                    placeholder="Search issues..."
                    value={issueSearch}
                    onChange={e => setIssueSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="insights-selector-list">
                  {loadingIssues ? (
                    <div className="insights-selector-empty">Loading issues...</div>
                  ) : filteredIssues.length === 0 ? (
                    <div className="insights-selector-empty">No issues found</div>
                  ) : (
                    filteredIssues.map(issue => (
                      <button key={issue.key} className="insights-selector-item" onClick={() => selectIssue(issue)}>
                        <span className="insights-selector-key">{issue.key}</span>
                        <span>{issue.fields?.summary}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* User Selector Panel */}
          {userSelector && (
            <div className="insights-selector-overlay" onClick={() => setUserSelector(null)}>
              <div className="insights-selector" onClick={e => e.stopPropagation()}>
                <div className="insights-selector-header">
                  <h3>Reassign To</h3>
                  <button className="insights-popup-close" onClick={() => setUserSelector(null)}>&#10005;</button>
                </div>
                <div className="insights-selector-search">
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="insights-selector-list">
                  {loadingUsers ? (
                    <div className="insights-selector-empty">Loading users...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="insights-selector-empty">No users found</div>
                  ) : (
                    filteredUsers.map(u => (
                      <button key={u.accountId || u.email} className="insights-selector-item" onClick={() => selectUser(u)}>
                        <span>{u.displayName}</span>
                        {u.email && <span className="insights-selector-meta">{u.email}</span>}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
